import React from "react";
import { Package } from "@phosphor-icons/react";

import { Empty, LoadingBlock, Pill } from "../../../design-system/components/desktop";
import { StatusPill } from "../../../design-system/components/mobile";
import { useViewport } from "../../../design-system/hooks/useViewport";
import { PanelRow, PanelRows } from "../../../design-system/patterns/settings";
import { getStatusColor, getStatusLabel } from "../../../utils/orderStatus";

/**
 * The buyer's four most recent orders. Pure.
 *
 * The status marker is the shared status vocabulary, read straight from
 * `utils/orderStatus.js`. `getStatusColor` returns one of five bucket names and
 * `Pill` takes exactly those names, so nothing translates between them and no
 * status can quietly fall through to a default tone.
 *
 * The old bespoke inline SVG box icon went; the glyph is Phosphor via the panel
 * row, like every other row on the page.
 *
 * The order total is rendered as the API returns it, which is already a Rand
 * string. Nothing here formats currency and nothing here may print a `$`.
 */

const formatOrderId = (orderId) => {
  if (!orderId) return "Unknown";
  return orderId.length > 12 ? orderId.substring(0, 8) : orderId;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Processing";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

function OrderStatus({ status }) {
  const { isDesktop } = useViewport();
  const tone = getStatusColor(status);
  const label = getStatusLabel(status);
  if (isDesktop) return <Pill tone={tone}>{label}</Pill>;
  return <StatusPill tone={tone === 'primary' ? 'accent' : tone}>{label}</StatusPill>;
}

const RecentOrdersSection = ({ orders = [], isLoading = false }) => {
  if (isLoading) return <LoadingBlock>Loading your recent orders</LoadingBlock>;

  if (!orders.length) {
    return (
      <Empty
        icon={Package}
        illustration="no-orders-yet"
        title="No orders yet"
        text="Anything you buy shows up here."
        inline
      />
    );
  }

  return (
    <PanelRows>
      {orders.slice(0, 4).map((order) => (
        <PanelRow
          key={order.order_id}
          icon={Package}
          title={`Order ${formatOrderId(order.order_id)}`}
          badge={<OrderStatus status={order.status || "pending"} />}
          detail={[
            formatDate(order.created_at),
            order.items?.length
              ? `${order.items.length} item${order.items.length === 1 ? "" : "s"}`
              : null,
            order.total || null,
          ]
            .filter(Boolean)
            .join(" · ")}
        />
      ))}
    </PanelRows>
  );
};

export default RecentOrdersSection;
